import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Facebook from '@/assets/socialMedia/Facebook.svg?react'
import Instagram from '@/assets/socialMedia/Instagram.svg?react'
import LinkedIn from '@/assets/socialMedia/LinkedIn.svg?react'
import Pinterest from '@/assets/socialMedia/Pinterest.svg?react'
import Reddit from '@/assets/socialMedia/Reddit.svg?react'
import TikTok from '@/assets/socialMedia/TikTok.svg?react'
import X from '@/assets/socialMedia/X.svg?react'
import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import useMobileMediaQuery from '@/hooks/useMobileMediaQuery'
import { socialMediaSheetSchema } from '@/utils/schemas'
import type { socialMediaSheetSchemaType } from '@/utils/schemas'

const PLATFORMS: ReadonlyArray<{
  name: keyof socialMediaSheetSchemaType
  Icon: React.FC<React.SVGProps<SVGSVGElement>>
  labelKey: string
  placeholderKey: string
}> = [
  {
    name: 'linkedIn',
    Icon: LinkedIn,
    labelKey: 'dashboard.runReport.socialMediaSheet.linkedIn',
    placeholderKey: 'dashboard.runReport.socialMediaSheet.linkedInPlaceholder',
  },
  {
    name: 'x',
    Icon: X,
    labelKey: 'dashboard.runReport.socialMediaSheet.x',
    placeholderKey: 'dashboard.runReport.socialMediaSheet.xPlaceholder',
  },
  {
    name: 'instagram',
    Icon: Instagram,
    labelKey: 'dashboard.runReport.socialMediaSheet.instagram',
    placeholderKey: 'dashboard.runReport.socialMediaSheet.instagramPlaceholder',
  },
  {
    name: 'facebook',
    Icon: Facebook,
    labelKey: 'dashboard.runReport.socialMediaSheet.facebook',
    placeholderKey: 'dashboard.runReport.socialMediaSheet.facebookPlaceholder',
  },
  {
    name: 'tikTok',
    Icon: TikTok,
    labelKey: 'dashboard.runReport.socialMediaSheet.tikTok',
    placeholderKey: 'dashboard.runReport.socialMediaSheet.tikTokPlaceholder',
  },
  {
    name: 'pinterest',
    Icon: Pinterest,
    labelKey: 'dashboard.runReport.socialMediaSheet.pinterest',
    placeholderKey: 'dashboard.runReport.socialMediaSheet.pinterestPlaceholder',
  },
  {
    name: 'reddit',
    Icon: Reddit,
    labelKey: 'dashboard.runReport.socialMediaSheet.reddit',
    placeholderKey: 'dashboard.runReport.socialMediaSheet.redditPlaceholder',
  },
]

interface SocialMediaSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: socialMediaSheetSchemaType
  onSave: (value: socialMediaSheetSchemaType) => void
}

const EMPTY_VALUES: socialMediaSheetSchemaType = {
  facebook: '',
  instagram: '',
  x: '',
  linkedIn: '',
  tikTok: '',
  pinterest: '',
  reddit: '',
}

export const SocialMediaSheet = ({
  open,
  onOpenChange,
  value,
  onSave,
}: SocialMediaSheetProps) => {
  const { t } = useTranslation()
  const isMobile = useMobileMediaQuery()

  const form = useForm<socialMediaSheetSchemaType>({
    resolver: zodResolver(socialMediaSheetSchema),
    defaultValues: { ...EMPTY_VALUES, ...value },
    mode: 'onChange',
  })

  const { handleSubmit, reset, control } = form

  useEffect(() => {
    if (open) reset({ ...EMPTY_VALUES, ...value })
  }, [open, value, reset])

  const handleSave = handleSubmit((data) => {
    onSave(data)
    onOpenChange(false)
  })

  const handleCancel = () => onOpenChange(false)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className="flex flex-col gap-4 max-md:h-[85vh] max-md:rounded-t-2xl sm:max-w-md"
      >
        <SheetHeader className="text-center sm:text-center">
          <SheetTitle>
            {t('dashboard.runReport.socialMediaSheet.title')}
          </SheetTitle>
          <SheetDescription>
            {t('dashboard.runReport.socialMediaSheet.description')}
          </SheetDescription>
        </SheetHeader>

        <FormProvider {...form}>
          <form
            onSubmit={handleSave}
            className="flex flex-1 min-h-0 flex-col gap-3 overflow-y-auto"
          >
            {PLATFORMS.map(({ name, Icon, labelKey, placeholderKey }) => (
              <FormField
                key={name}
                control={control}
                name={name}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1">
                    <FormLabel className="flex items-center gap-2 text-sm font-medium text-neutralColor">
                      <Icon className="h-4 w-4" />
                      {t(labelKey)}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t(placeholderKey)}
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </form>
        </FormProvider>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={handleCancel}>
            {t('dashboard.runReport.socialMediaSheet.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!form.formState.isValid}
          >
            {t('dashboard.runReport.socialMediaSheet.confirm')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
